import store from 'state/store';
import { pluginApi } from 'services/api';

import {
  Product,
  BillingPog,
  Campaign,
  Potentializer,
  Infos,
  Biological,
} from 'state/modules/goals/types';

import {
  transformCampaigns,
  transformProducts,
  transformBillingPog,
  transformInfos,
  transformPotentializers,
  transformCurrentCampaing,
  transformBiological,
} from './transformers';

const getCampaignIdFromState = (): number | null => {
  const state = store.getState();
  return state.goals.selectedCampaignId;
};

export interface FetchCurrentCampaignService {
  id: number;
  title: string;
  description: string;
  Grupo?: string;
}

export const fetchCurrentCampaignService = async (): Promise<Campaign | null> => {
  const { data } = await pluginApi.get<FetchCurrentCampaignService>(
    '/campaigns/current',
  );

  return transformCurrentCampaing(data);
};

export const fetchCurrentCampaignTerraService = async (): Promise<Campaign | null> => {
  const { data } = await pluginApi.get<FetchCurrentCampaignService>(
    '/campaigns/currentTerra',
  );

  return transformCurrentCampaing(data);
};

export interface FetchCampaignsService {
  id: number;
  title: string;
  description: string;
  Grupo?: string;
  GrupoID?: number;
}
export const fetchCampaignsService = async (): Promise<Campaign[] | null> => {
  const { data } = await pluginApi.get<{
    campaigns: FetchCampaignsService[];
  }>('/statement/campaigns');

  return transformCampaigns(data.campaigns);
};

interface BillingPogService {
  id: number;
  value: number;
  owner_ref: string;
  category: string;
  period: string;
  generated_at: string;
  pcp: {
    color: string;
    position: number;
  };
  balance_unit: {
    id: number;
    name: string;
    description: string;
  };
  progress: {
    value: number;
    percentage: number;
  };
}
export interface FetchBillingPogService {
  margem_adicional: {
    meta: string;
    realizado: string;
    porcentagem: number;
  };
  faturamento: BillingPogService;
  pog: BillingPogService;
  devolução: {
    value: string;
    progress: {
      value: number;
      checked: boolean;
    };
    punctuality: string | null;
  };
}


export const fetchBillingPogService = async (establishmentId?: number | string): Promise<BillingPog | null> => {
  const campaignId = getCampaignIdFromState();
  console.log('service establishmentId', establishmentId);
  let url = '/goals/performance';
  const queryParams = new URLSearchParams();

  if (campaignId) {
    queryParams.append('campaign_id', campaignId.toString());
  }

  if (establishmentId) {
    queryParams.append('establishment_id', establishmentId.toString());
  }

  const queryString = queryParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  const { data } = await pluginApi.get<FetchBillingPogService>(url);

  return transformBillingPog(data);
};

export interface FetchBiologicalService {
  invoice: {
    goal: number;
    percentage: number;
    realized: number;
  };
  pog: {
    goal: number;
    realized: number;
    percentage: number;
  };
  margem_adicional: {
    meta: string;
    realizado: string;
    porcentagem: number;
  };
}

export const fetchBiologicalService = async (): Promise<Biological | null> => {
  const campaignId = getCampaignIdFromState();
  let url = '/goals/performance-biological';
  if (campaignId) {
    url += `?campaign_id=${campaignId}`;
  }
  const { data } = await pluginApi.get<FetchBiologicalService>(url);

  return transformBiological(data);
};
export interface FetchPotentializersService {
  id: number;
  value: number;
  owner_ref: string;
  category: string;
  period: string;
  generated_at: string;
  pcp: {
    color: string;
    position: number;
  };
  balance_unit: {
    id: number;
    name: string;
    description: string;
  };
  progress: {
    value: number;
    percentage: number;
  };
}

export const fetchPotentializersService = async (establishmentId?: number | string): Promise<
  Potentializer[] | null
> => {
  const campaignId = getCampaignIdFromState();
  let url = '/goals/focalProducts';
  const queryParams = new URLSearchParams();
  console.log('potenctializer establishmentId', establishmentId);
  if (campaignId) {
    queryParams.append('campaign_id', campaignId.toString());
  }

  if (establishmentId) {
    queryParams.append('establishment_id', establishmentId.toString());
  }

  const queryString = queryParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  const { data } = await pluginApi.get<FetchPotentializersService[]>(url);

  return transformPotentializers(data);
};

export interface FetchInfosService {
  potential: {
    id: number;
    establishment_id: number;
    campaign_id: number;
    value: number;
    generated_at: string;
    potential_import_id: number;
    created: string;
    modified: string;
  };
  generationDemand: {
    value: number;
    balance_unit: {
      id: number;
      name: string;
      description: string;
    };
  };
  businessGeneration: {
    value: number;
    balance_unit: {
      id: number;
      name: string;
      description: string;
    };
  };
  biological: {
    value: number;
    balance_unit: {
      id: number;
      name: string;
      description: string;
    };
  };
  additionalMargin: {
    value: number;
    balance_unit: {
      id: number;
      name: string;
      description: string;
    };
  };
  importedPoints: {
    total: number;
    name: string;
    description: string;
  }[];
  last_update: string;
  last_update_invoicing: string;
  last_update_pog: string;
  full_performance_report: string;
}
export const fetchInfosService = async (): Promise<Infos | null> => {
  const campaignId = getCampaignIdFromState();
  let url = '/goals/info';
  if (campaignId) {
    url += `?campaign_id=${campaignId}`;
  }
  const { data } = await pluginApi.get<FetchInfosService>(url);
  return transformInfos(data);
};

export interface FetchProductsService {
  id: number;
  name: string;
  value: number;
  volume: number;
  position: string;
}

export const fetchTopPurchasingProductsService = async (): Promise<
  Product[] | null
> => {
  const campaignId = getCampaignIdFromState();
  let url = '/goals/top-ten-purchases';
  if (campaignId) {
    url += `?campaign_id=${campaignId}`;
  }
  const { data } = await pluginApi.get<FetchProductsService[]>(url);

  return transformProducts(data);
};

export const fetchTopSellingProductsService = async (): Promise<
  Product[] | null
> => {
  const campaignId = getCampaignIdFromState();
  let url = '/goals/top-ten-sellers';
  if (campaignId) {
    url += `?campaign_id=${campaignId}`;
  }
  const { data } = await pluginApi.get<FetchProductsService[]>(url);

  return transformProducts(data);
};
